import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Loader2, FileText, BookOpen, ClipboardList, LayoutGrid, Smartphone, PackageSearch, Network, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DocSidebar, SECTIONS } from "@/components/proposal/DocSidebar";
import { ConfigPanel } from "@/components/proposal/ConfigPanel";
import { DocumentPreview } from "@/components/proposal/DocumentPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProposalStudio() {
  const [content, setContent] = useState(null);
  const [diagrams, setDiagrams] = useState(null);
  const [branding, setBranding] = useState(null);
  const [versions, setVersions] = useState([]);
  const [activeId, setActiveId] = useState("sec-cover");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingSpec, setGeneratingSpec] = useState(false);
  const [generatingManual, setGeneratingManual] = useState(false);
  const [generatingWalkthrough, setGeneratingWalkthrough] = useState(false);
  const [generatingCards, setGeneratingCards] = useState(false);
  const [generatingMobitech, setGeneratingMobitech] = useState(false);
  const [generatingNonSerial, setGeneratingNonSerial] = useState(false);
  const [generatingAxCrm, setGeneratingAxCrm] = useState(false);
  const [generatingTraining, setGeneratingTraining] = useState(false);

  const loadVersions = useCallback(async () => {
    const { data } = await axios.get(`${API}/versions`);
    setVersions(data);
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await axios.get(`${API}/proposal`);
      setContent(data.content);
      setBranding(data.branding);
      setDiagrams(data.diagrams);
    })();
    loadVersions();
  }, [loadVersions]);

  const handleNavigate = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(id);
  };

  useEffect(() => {
    const canvas = document.getElementById("doc-canvas");
    if (!canvas) return;
    const onScroll = () => {
      let current = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= 160) current = s.id;
      }
      setActiveId(current);
    };
    canvas.addEventListener("scroll", onScroll);
    return () => canvas.removeEventListener("scroll", onScroll);
  }, [content]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/branding`, branding);
      toast.success("Branding saved", { description: "Your changes are stored and ready to export." });
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const { data } = await axios.post(`${API}/reset-branding`);
    setBranding(data.branding);
    toast.info("Branding reset to defaults");
  };

  const handleGenerate = async () => {
    setGenerating(true);
    toast.loading("Generating PDF…", { id: "gen" });
    try {
      await axios.put(`${API}/branding`, branding);
      const { data } = await axios.post(`${API}/generate`);
      await loadVersions();
      window.open(`${process.env.REACT_APP_BACKEND_URL}${data.download_url}`, "_blank");
      toast.success("PDF ready", {
        id: "gen",
        description: `${data.filename} · ${(data.size / 1024 / 1024).toFixed(1)} MB`,
        action: {
          label: "Re-download",
          onClick: () => window.open(`${process.env.REACT_APP_BACKEND_URL}${data.download_url}`, "_blank"),
        },
      });
    } catch (e) {
      toast.error("Generation failed", { id: "gen" });
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateSpec = async () => {
    setGeneratingSpec(true);
    toast.loading("Generating Feature Spec…", { id: "spec" });
    try {
      const { data } = await axios.post(`${API}/generate-featurespec`);
      await loadVersions();
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      window.open(url, "_blank");
      toast.success("Feature Spec ready", {
        id: "spec",
        description: `${data.filename} · ${(data.size / 1024 / 1024).toFixed(2)} MB`,
        action: { label: "Re-download", onClick: () => window.open(url, "_blank") },
      });
    } catch (e) {
      toast.error("Feature Spec generation failed", { id: "spec" });
    } finally {
      setGeneratingSpec(false);
    }
  };

  const handleGenerateManual = async () => {
    setGeneratingManual(true);
    toast.loading("Generating User Manual…", { id: "manual" });
    try {
      const { data } = await axios.post(`${API}/generate-usermanual`);
      await loadVersions();
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      window.open(url, "_blank");
      toast.success("User Manual ready", {
        id: "manual",
        description: `${data.filename} · ${(data.size / 1024 / 1024).toFixed(2)} MB`,
        action: { label: "Re-download", onClick: () => window.open(url, "_blank") },
      });
    } catch (e) {
      toast.error("User Manual generation failed", { id: "manual" });
    } finally {
      setGeneratingManual(false);
    }
  };

  const handleGenerateWalkthrough = async () => {
    setGeneratingWalkthrough(true);
    toast.loading("Generating Pilot Walkthrough…", { id: "walk" });
    try {
      const { data } = await axios.post(`${API}/generate-pilot-walkthrough`);
      await loadVersions();
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      window.open(url, "_blank");
      toast.success("Pilot Walkthrough ready", {
        id: "walk",
        description: `${data.filename} · ${(data.size / 1024 / 1024).toFixed(2)} MB`,
        action: { label: "Re-download", onClick: () => window.open(url, "_blank") },
      });
    } catch (e) {
      toast.error("Pilot Walkthrough generation failed", { id: "walk" });
    } finally {
      setGeneratingWalkthrough(false);
    }
  };

  const handleGenerateCards = async () => {
    setGeneratingCards(true);
    toast.loading("Generating Quick Cards…", { id: "cards" });
    try {
      const { data } = await axios.post(`${API}/generate-quickcards`);
      await loadVersions();
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      window.open(url, "_blank");
      toast.success("Quick Cards ready", {
        id: "cards",
        description: `${data.filename} · ${(data.size / 1024 / 1024).toFixed(2)} MB`,
        action: { label: "Re-download", onClick: () => window.open(url, "_blank") },
      });
    } catch (e) {
      toast.error("Quick Cards generation failed", { id: "cards" });
    } finally {
      setGeneratingCards(false);
    }
  };

  const handleGenerateDoc = async (endpoint, label, toastId, setBusy) => {
    setBusy(true);
    toast.loading(`Generating ${label}…`, { id: toastId });
    try {
      const { data } = await axios.post(`${API}/${endpoint}`);
      await loadVersions();
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      window.open(url, "_blank");
      toast.success(`${label} ready`, {
        id: toastId,
        description: `${data.filename} · ${(data.size / 1024 / 1024).toFixed(2)} MB`,
        action: { label: "Re-download", onClick: () => window.open(url, "_blank") },
      });
    } catch (e) {
      toast.error(`${label} generation failed`, { id: toastId });
    } finally {
      setBusy(false);
    }
  };

  if (!content || !branding || !diagrams) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F4F5F7]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00857C]" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#F4F5F7]">
      <DocSidebar activeId={activeId} onNavigate={handleNavigate} versions={versions} />
      <ConfigPanel branding={branding} setBranding={setBranding} onSave={handleSave} onReset={handleReset} saving={saving} />

      <main className="flex-1 relative">
        {/* Floating toolbar */}
        <div className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 border-b border-slate-200 px-8 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-500">
            <FileText className="h-4 w-4 text-[#00857C]" />
            <span className="overline text-slate-500">Live Preview · {branding.version}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="download-training-exercise-button"
              onClick={() => handleGenerateDoc("generate-training-exercise", "Pilot Training Exercise", "training", setGeneratingTraining)}
              disabled={generatingTraining}
              variant="outline"
              className="rounded-sm border-[#7c3aed] text-[#7c3aed] hover:bg-[#7c3aed] hover:text-white font-head px-4 transition-colors duration-200"
            >
              {generatingTraining ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GraduationCap className="h-4 w-4 mr-2" />}
              Training Exercise
            </Button>
            <Button
              data-testid="download-ax-crm-integration-button"
              onClick={() => handleGenerateDoc("generate-ax-crm-integration", "AX & CRM Integration Spec", "axcrm", setGeneratingAxCrm)}
              disabled={generatingAxCrm}
              variant="outline"
              className="rounded-sm border-[#0f766e] text-[#0f766e] hover:bg-[#0f766e] hover:text-white font-head px-4 transition-colors duration-200"
            >
              {generatingAxCrm ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Network className="h-4 w-4 mr-2" />}
              AX &amp; CRM Spec
            </Button>
            <Button
              data-testid="download-mobitech-integration-button"
              onClick={() => handleGenerateDoc("generate-mobitech-integration", "Mobitech Integration Blueprint", "mobi", setGeneratingMobitech)}
              disabled={generatingMobitech}
              variant="outline"
              className="rounded-sm border-[#0369a1] text-[#0369a1] hover:bg-[#0369a1] hover:text-white font-head px-4 transition-colors duration-200"
            >
              {generatingMobitech ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Smartphone className="h-4 w-4 mr-2" />}
              Mobitech Integration
            </Button>
            <Button
              data-testid="download-nonserialised-report-button"
              onClick={() => handleGenerateDoc("generate-nonserialised-report", "Non-Serialised Components Report Design", "nonser", setGeneratingNonSerial)}
              disabled={generatingNonSerial}
              variant="outline"
              className="rounded-sm border-[#be123c] text-[#be123c] hover:bg-[#be123c] hover:text-white font-head px-4 transition-colors duration-200"
            >
              {generatingNonSerial ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PackageSearch className="h-4 w-4 mr-2" />}
              Non-Serialised Report
            </Button>
            <Button
              data-testid="download-walkthrough-button"
              onClick={handleGenerateWalkthrough}
              disabled={generatingWalkthrough}
              variant="outline"
              className="rounded-sm border-[#b45309] text-[#b45309] hover:bg-[#b45309] hover:text-white font-head px-4 transition-colors duration-200"
            >
              {generatingWalkthrough ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardList className="h-4 w-4 mr-2" />}
              Pilot Walkthrough
            </Button>
            <Button
              data-testid="download-quickcards-button"
              onClick={handleGenerateCards}
              disabled={generatingCards}
              variant="outline"
              className="rounded-sm border-[#7c3aed] text-[#7c3aed] hover:bg-[#7c3aed] hover:text-white font-head px-4 transition-colors duration-200"
            >
              {generatingCards ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LayoutGrid className="h-4 w-4 mr-2" />}
              Quick Cards
            </Button>
            <Button
              data-testid="download-usermanual-button"
              onClick={handleGenerateManual}
              disabled={generatingManual}
              variant="outline"
              className="rounded-sm border-[#00857C] text-[#00857C] hover:bg-[#00857C] hover:text-white font-head px-4 transition-colors duration-200"
            >
              {generatingManual ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BookOpen className="h-4 w-4 mr-2" />}
              User Manual
            </Button>
            <Button
              data-testid="download-featurespec-button"
              onClick={handleGenerateSpec}
              disabled={generatingSpec}
              variant="outline"
              className="rounded-sm border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white font-head px-4 transition-colors duration-200"
            >
              {generatingSpec ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
              Feature Spec
            </Button>
            <Button
              data-testid="download-pdf-button"
              onClick={handleGenerate}
              disabled={generating}
              className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] text-white font-head px-5 transition-colors duration-200"
            >
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              {generating ? "Generating…" : "Download PDF"}
            </Button>
          </div>
        </div>

        <div id="doc-canvas" className="doc-canvas overflow-y-auto p-8 flex justify-center" style={{ height: "calc(100vh - 57px)" }}>
          <DocumentPreview content={content} branding={branding} diagrams={diagrams} />
        </div>
      </main>
    </div>
  );
}
